import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import moment from 'moment-timezone';
import Select from 'react-select';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import { Spinner } from 'reactstrap';
import { useQuery } from 'react-query';
import { teamsReportingSelectStyles } from './TeamsReporting.constants';
import styles from './TeamsReporting.module.scss';
import { FiClock, FiTrendingUp } from 'react-icons/fi';
import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import { useMemberCompanyTimezones } from '@/hooks/useMemberCompanyTimezones';
import memberSessionsService from '@/services/memberSessions';
import { queryKeys } from '@/config/queryKeys';
import { Dropdown } from '@/components/Dropdown';
import { normalizeIdleForDisplay } from '@/utils/idleDisplayUtils';
import {
  useDashboardOverviewPopSync,
  useDashboardOverviewUrlWrite,
} from '@/hooks/useDashboardOverviewUrlSync';
import {
  getDashboardOverviewFiltersFromSearch,
  getInitialReportDateRangeFromSearch,
} from '@/utils/dashboardOverviewUrlFilters';

am4core.useTheme(am4themes_animated);

function formatHoursMinutes(hours, minutes) {
  const h = Number(hours) || 0;
  const m = Number(minutes) || 0;
  const totalMins = h * 60 + m;
  const hrs = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}

export const TeamsReportingComponent = () => {
  const history = useHistory();
  const location = useLocation();
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const [range, setRange] = useState(() =>
    getInitialReportDateRangeFromSearch(
      typeof window !== 'undefined' ? window.location.search : ''
    )
  );
  const [selectedTeam, setSelectedTeam] = useState(null);

  const activeCompany = useSelector(
    (state) => state.auth?.profile?.activeCompany
  );
  const currentUserId = useSelector((state) => state.auth?.profile?.id);
  const teamsFromRedux = useSelector((state) => state.team?.teams);
  const teamContextId = useSelector((state) => {
    const t = state.team;
    return (
      t?.selectedTeamID ||
      t?.active?.activeTeamid ||
      t?.teams?.data?.[0]?.activeTeamid
    );
  });

  const filtersFromUrl = useMemo(
    () => getDashboardOverviewFiltersFromSearch(location.search),
    [location.search]
  );
  const [ignoreUrlTimezone, setIgnoreUrlTimezone] = useState(false);
  const userOverrideTimezoneRef = useRef(false);
  const [, setUrlMemberPlaceholder] = useState(null);
  const membersOptionsRef = useRef([]);
  const teamOptionsRef = useRef([]);

  const urlTimezoneForHook = useMemo(
    () => (!ignoreUrlTimezone && filtersFromUrl.tz ? filtersFromUrl.tz : null),
    [ignoreUrlTimezone, filtersFromUrl.tz]
  );

  const { selectedTimezone, memberTimezone, companyTimezone } =
    useMemberCompanyTimezones({
      companyId: activeCompany?.companyId,
      teamId: selectedTeam?.value,
      memberId: currentUserId,
      initialTimezone: urlTimezoneForHook,
      autoSelectMemberTimezone: true,
      userOverrideTimezoneRef,
    });

  const timezone =
    selectedTimezone || companyTimezone || memberTimezone || moment.tz.guess();

  useDashboardOverviewPopSync({
    history,
    setDateRange: setRange,
    setIgnoreUrlTimezone,
    setSelectedMember: setUrlMemberPlaceholder,
    membersOptionsRef,
    teamOptionsRef,
    setSelectedTeam,
  });

  useDashboardOverviewUrlWrite({
    teamId: teamContextId,
    memberId: currentUserId,
    teamEntityId: selectedTeam?.value,
    dateRange: range,
    selectedTimezone: timezone,
    history,
    location,
  });

  const teamDropdownOptions = useMemo(() => {
    const data = teamsFromRedux?.data ?? [];
    return data.map((it) => ({ label: it.Team, value: it.activeTeamid }));
  }, [teamsFromRedux?.data]);

  useEffect(() => {
    teamOptionsRef.current = teamDropdownOptions;
  }, [teamDropdownOptions]);

  // Hydrate from URL only while no selection yet. After that, URL updates come
  // from useDashboardOverviewUrlWrite (REPLACE); re-applying filtersFromUrl.team
  // here would race and flicker the team dropdown (same pattern as member on dashboard).
  useEffect(() => {
    if (!teamDropdownOptions.length) return;
    if (selectedTeam) return;

    if (filtersFromUrl.team) {
      const opt = teamDropdownOptions.find(
        (o) => o.value === filtersFromUrl.team
      );
      if (opt) {
        setSelectedTeam(opt);
        return;
      }
    }
    setSelectedTeam(teamDropdownOptions[0]);
  }, [teamDropdownOptions, filtersFromUrl.team, selectedTeam]);

  const reportQueryEnabled = !!(
    selectedTeam?.value &&
    range.start &&
    range.end &&
    timezone
  );
  const {
    data: reportData,
    isFetching: reportLoading,
    isError: reportError,
    error: reportErrorDetail,
  } = useQuery(
    queryKeys.teamTimeTracking.report(
      selectedTeam?.value,
      range.start,
      range.end,
      timezone
    ),
    () =>
      memberSessionsService.fetchTeamTimeTrackingData({
        teamId: selectedTeam.value,
        startDate: range.start,
        endDate: range.end,
        timezone,
      }),
    {
      enabled: reportQueryEnabled,
      select: (res) => (res.success ? res.data : null),
    }
  );

  const chartData = useMemo(() => {
    if (!reportData?.dailyBreakdown?.length) return [];
    return reportData.dailyBreakdown.map((day) => {
      const hours = (day.totalHours || 0) + (day.totalMinutes || 0) / 60;
      return {
        date: new Date(day.dateId + 'T12:00:00'),
        hours: Math.round(hours * 100) / 100,
      };
    });
  }, [reportData?.dailyBreakdown]);

  const totalTimeFormatted = useMemo(() => {
    if (!reportData) return '--:--';
    return formatHoursMinutes(reportData.totalHours, reportData.totalMinutes);
  }, [reportData]);

  const productiveFormatted = useMemo(() => {
    if (!reportData) return '--:--';
    return formatHoursMinutes(
      reportData.productiveHours,
      reportData.productiveMinutes
    );
  }, [reportData]);

  const idleFormatted = useMemo(() => {
    if (!reportData) return '--:--';
    const totalMin =
      (reportData.totalHours || 0) * 60 + (reportData.totalMinutes || 0);
    const idleMin =
      (reportData.idleHours || 0) * 60 + (reportData.idleMinutes || 0);
    const { displayIdle } = normalizeIdleForDisplay(totalMin, idleMin);
    return formatHoursMinutes(Math.floor(displayIdle / 60), displayIdle % 60);
  }, [reportData]);

  useEffect(() => {
    if (chartRef.current) {
      const chart = am4core.create(chartRef.current, am4charts.XYChart);
      chartInstance.current = chart;
      chart.data = [];
      // X Axis — Date
      const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
      dateAxis.title.text = 'Date';
      dateAxis.baseInterval = { timeUnit: 'day', count: 1 };
      dateAxis.renderer.minGridDistance = 40;
      dateAxis.dateFormats.setKey('day', 'EEE MMM dd, yyyy');
      dateAxis.periodChangeDateFormats.setKey('day', 'EEE MMM dd, yyyy');
      dateAxis.fontSize = 12;
      dateAxis.renderer.grid.template.disabled = true;
      // Y Axis — Hours
      const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      valueAxis.title.text = 'Hours';
      valueAxis.min = 0;
      valueAxis.max = 24;
      valueAxis.fontSize = 12;
      valueAxis.renderer.labels.template.adapter.add(
        'text',
        function (text, target) {
          const value = target.dataItem.value;

          if (value !== undefined) {
            return `${String(value).padStart(2, '0')}:00`;
          }

          return text;
        }
      );

      // Bar series
      const series = chart.series.push(new am4charts.ColumnSeries());
      series.dataFields.dateX = 'date';
      series.dataFields.valueY = 'hours';
      series.name = 'Hours';
      series.tooltipText = '{valueY} hours';

      // Style bars
      series.columns.template.width = am4core.percent(10);
      series.columns.template.cornerRadiusTopLeft = 5;
      series.columns.template.cornerRadiusTopRight = 5;
      series.columns.template.fill = am4core.color('#3363f5');
      // Cursor (hover interaction)
      chart.cursor = new am4charts.XYCursor();

      return () => {
        chart.dispose();
      };
    }
  }, []);
  useEffect(() => {
    if (chartInstance.current) {
      chartInstance.current.data = chartData;
    }
  }, [chartData]);
  return (
    <div className={styles.teamsReportingPage}>
      {' '}
      <div className={styles.pageHeader}>
        <h1 className="title">Department's Reporting</h1>
        <div className={styles.filtersContainer}>
          <div className={styles.filterItem}>
            <Dropdown
              options={teamDropdownOptions}
              value={selectedTeam}
              onChange={(opt) => {
                userOverrideTimezoneRef.current = false;
                setIgnoreUrlTimezone(true);
                setSelectedTeam(opt);
              }}
              label={'Department'}
            />
          </div>

          <div className={styles.filterItem}>
            <Text
              variant={textVariantsEnum.Text_13}
              fontWeight={fontWeightEnum.Normal}
              className={styles.dateRangeLabel}
            >
              Date Range
            </Text>
            <DateRangePicker
              startDate={moment(range.start)}
              endDate={moment(range.end)}
              onApply={(_event, { startDate, endDate }) => {
                setIgnoreUrlTimezone(true);
                setRange({
                  start: startDate.format('YYYY-MM-DD'),
                  end: endDate.format('YYYY-MM-DD'),
                });
              }}
            >
              <button type="button" className={styles.dateRangeButton}>
                {moment(range.start).format('DD MMM YYYY')} -{' '}
                {moment(range.end).format('DD MMM YYYY')}
              </button>
            </DateRangePicker>
          </div>
        </div>
        {reportError && (
          <div className={styles.metricsContainer}>
            <Text variant={textVariantsEnum.Text_sm}>
              {reportErrorDetail?.message || 'Failed to load report'}
            </Text>
          </div>
        )}
        {reportLoading && (
          <div className={styles.reportLoader}>
            <Spinner color="primary" />
            <Text
              variant={textVariantsEnum.Text_sm}
              className={styles.reportLoaderText}
            >
              Loading report...
            </Text>
          </div>
        )}
        <div className={styles.metricsContainer}>
          <div className={styles.metricItem}>
            <div className={styles.metricItemIcon}>
              <FiClock color="#ffffff" />
            </div>
            <div className={styles.metricItemTitle}>
              <Text variant={textVariantsEnum.Text_sm}>Total Time</Text>
              <Text
                variant={textVariantsEnum.Text_md}
                fontWeight={fontWeightEnum.Bold}
              >
                {reportLoading ? '...' : totalTimeFormatted}
              </Text>
            </div>
          </div>
          <div className={styles.metricItem}>
            <div className={styles.metricItemIcon}>
              <FiTrendingUp color="#ffffff" />
            </div>
            <div className={styles.metricItemTitle}>
              <Text variant={textVariantsEnum.Text_sm}>
                Productive activity
              </Text>
              <Text
                variant={textVariantsEnum.Text_md}
                fontWeight={fontWeightEnum.Bold}
              >
                {reportLoading ? '...' : productiveFormatted}
              </Text>
            </div>
          </div>
          <div className={styles.metricItem}>
            <div className={styles.metricItemIcon}>
              <FiTrendingUp color="#ffffff" />
            </div>
            <div className={styles.metricItemTitle}>
              <Text variant={textVariantsEnum.Text_sm}>Idle activity</Text>
              <Text
                variant={textVariantsEnum.Text_md}
                fontWeight={fontWeightEnum.Bold}
              >
                {reportLoading ? '...' : idleFormatted}
              </Text>
            </div>
          </div>
        </div>
        <div className={styles.chartContainer}>
          <Text
            variant={textVariantsEnum.Text_md}
            fontWeight={fontWeightEnum.Bold}
          >
            Chart
          </Text>
          {reportLoading && !reportData && (
            <Text variant={textVariantsEnum.Text_sm}>Loading chart...</Text>
          )}
          <div ref={chartRef} style={{ width: '100%', height: '400px' }} />
        </div>
      </div>
    </div>
  );
};
